import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import { getSettings, updateSettings } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
    FileText, 
    Save, 
    Home, 
    ListChecks, 
    MapPin, 
    Search,
    Star,
    ArrowRight,
    Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Text groups configuration
const TEXT_GROUPS = [
    {
        id: 'landing',
        title: 'Startseite',
        icon: Home,
        color: 'bg-blue-50 text-blue-600',
        texts: [
            { key: 'landing_slogan', label: 'Hauptüberschrift (groß)', placeholder: 'Probiere Dein Wunschinstrument!', multiline: false },
            { key: 'landing_subslogan', label: 'Zweite Überschrift', placeholder: 'Finde dein Instrument!', multiline: false },
            { key: 'landing_subtitle', label: 'Untertitel', placeholder: 'Woche des offenen Unterrichts', multiline: false },
            { key: 'landing_cta', label: 'Haupt-Button', placeholder: "Los geht's!", multiline: false },
            { key: 'landing_location_cta', label: 'Standort-Button', placeholder: 'Standort wählen', multiline: false },
            { key: 'landing_info_text', label: 'Info-Text', placeholder: 'Melde dich jetzt an...', multiline: true },
        ]
    },
    {
        id: 'wizard_step1',
        title: 'Wizard - Instrumentenauswahl',
        icon: ListChecks,
        color: 'bg-violet-50 text-violet-600',
        texts: [
            { key: 'wizard_step1_title', label: 'Überschrift', placeholder: 'Welches Instrument möchtest du ausprobieren?', multiline: false },
            { key: 'wizard_step1_subtitle', label: 'Untertitel', placeholder: 'Du kannst auch mehrere auswählen', multiline: false },
            { key: 'wizard_step1_skip', label: '"Überspringen" Button', placeholder: 'Ich weiß noch nicht – alle Angebote zeigen', multiline: false },
            { key: 'wizard_step1_search', label: 'Suchfeld Platzhalter', placeholder: 'Instrument suchen...', multiline: false },
        ]
    },
    {
        id: 'wizard_step2',
        title: 'Wizard - Gemeindeauswahl',
        icon: MapPin,
        color: 'bg-teal-50 text-teal-600',
        texts: [
            { key: 'wizard_step2_title', label: 'Überschrift', placeholder: 'Wo wohnst du?', multiline: false },
            { key: 'wizard_step2_subtitle', label: 'Untertitel', placeholder: 'Wähle deine Gemeinde', multiline: false },
        ]
    },
    {
        id: 'results',
        title: 'Ergebnisseite',
        icon: Search,
        color: 'bg-orange-50 text-orange-600',
        description: 'Platzhalter: {instrument}, {gemeinde}',
        texts: [
            { key: 'wizard_no_results', label: 'Keine Ergebnisse Titel', placeholder: 'Keine Angebote gefunden', multiline: false },
            { key: 'wizard_instrument_not_available', label: 'Instrument nicht verfügbar', placeholder: 'In {gemeinde} wird in dieser Woche leider kein Schnuppertermin für {instrument} angeboten.', multiline: false },
            { key: 'wizard_instrument_available_in', label: 'Verfügbar in (Label)', placeholder: 'Verfügbar in:', multiline: false },
            { key: 'wizard_instrument_nowhere', label: 'Nirgends verfügbar', placeholder: 'Leider nirgends verfügbar diese Woche.', multiline: false },
            { key: 'wizard_no_results_generic', label: 'Keine Ergebnisse (allgemein)', placeholder: 'Leider gibt es in {gemeinde} gerade keine Angebote.', multiline: true },
            { key: 'wizard_alternative_hint', label: 'Hinweis auf Nachbargemeinden', placeholder: 'Aber in einer Nachbargemeinde wartet vielleicht schon dein erster Ton auf dich 🎶', multiline: false },
            { key: 'wizard_show_all_offers', label: '"Alle Angebote" Button', placeholder: 'Alle Angebote in {gemeinde}', multiline: false },
            { key: 'wizard_other_locations', label: 'Andere Standorte Titel', placeholder: '{instrument} in anderen Gemeinden', multiline: false },
        ]
    },
    {
        id: 'buttons',
        title: 'Buttons & Navigation',
        icon: ArrowRight,
        color: 'bg-gray-100 text-gray-600',
        texts: [
            { key: 'btn_back', label: 'Zurück-Button', placeholder: 'Zurück', multiline: false },
            { key: 'btn_continue', label: 'Weiter-Button', placeholder: 'Weiter', multiline: false },
            { key: 'btn_show_offers', label: 'Angebote anzeigen', placeholder: 'Angebote anzeigen', multiline: false },
            { key: 'btn_filter_all', label: 'Filter "Alle"', placeholder: 'Alle', multiline: false },
        ]
    },
    {
        id: 'lehrer_portal',
        title: 'Lehrer-Portal',
        icon: Star,
        color: 'bg-amber-50 text-amber-600',
        texts: [
            { key: 'lehrer_portal_title', label: 'Portal-Titel', placeholder: 'Lehrer-Portal', multiline: false },
            { key: 'lehrer_portal_my_instruments', label: 'Meine Instrumente', placeholder: 'Deine Instrumente', multiline: false },
            { key: 'lehrer_portal_my_offers', label: 'Meine Angebote', placeholder: 'Meine Angebote', multiline: false },
            { key: 'lehrer_portal_new_offer', label: 'Neues Angebot Button', placeholder: 'Neues Angebot eintragen', multiline: false },
        ]
    },
    {
        id: 'footer',
        title: 'Footer & Kontakt',
        icon: Info,
        color: 'bg-slate-100 text-slate-600',
        texts: [
            { key: 'contact_text', label: 'Kontakt-Text', placeholder: 'Bei Fragen kontaktiere uns unter:', multiline: false },
            { key: 'footer_text', label: 'Footer-Text', placeholder: '© 2025 Musikschule Region Amstetten', multiline: false },
        ]
    },
];

export function EinstellungenTexte() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(new Set(['landing']));
    const [hasChanges, setHasChanges] = useState(false);
    
    useEffect(() => {
        fetchSettings();
    }, []);
    
    const fetchSettings = async () => {
        try {
            const response = await getSettings();
            setSettings(response.data);
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            toast.error('Einstellungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSave = async () => {
        try {
            setSaving(true);
            await updateSettings(settings);
            toast.success('Texte gespeichert');
            setHasChanges(false);
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };
    
    const updateUiText = (key, value) => {
        setSettings({
            ...settings,
            ui_texts: {
                ...(settings?.ui_texts || {}),
                [key]: value
            }
        });
        setHasChanges(true);
    };
    
    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };
    
    const getTextValue = (key) => {
        return settings?.ui_texts?.[key] || '';
    };
    
    if (loading) {
        return (
            <>
                <AdminHeader title="Textverwaltung" />
                <div className="admin-content">
                    <div className="max-w-3xl">
                        <div className="space-y-4">
                            {[1,2,3].map(i => (
                                <div key={i} className="bg-card rounded-2xl p-6 animate-pulse">
                                    <div className="h-6 bg-muted rounded w-48 mb-4" />
                                    <div className="space-y-3">
                                        <div className="h-10 bg-muted rounded w-full" />
                                        <div className="h-10 bg-muted rounded w-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
            <AdminHeader title="Textverwaltung">
                <Button 
                    onClick={handleSave} 
                    disabled={saving || !hasChanges}
                    className={cn("rounded-xl", hasChanges && "bg-green-600 hover:bg-green-700")}
                >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Speichert...' : 'Änderungen speichern'}
                </Button>
            </AdminHeader>
            
            <div className="admin-content">
                <div className="max-w-3xl space-y-4">
                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
                        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                            <p className="font-medium">Texte anpassen</p>
                            <p className="mt-1">
                                Hier können Sie alle Texte der öffentlichen Seite bearbeiten. 
                                Nutzen Sie Platzhalter wie <code className="bg-blue-100 px-1 rounded">{'{instrument}'}</code> oder <code className="bg-blue-100 px-1 rounded">{'{gemeinde}'}</code> für dynamische Inhalte.
                            </p>
                        </div>
                    </div>
                    
                    {/* Text Groups */}
                    {TEXT_GROUPS.map(group => {
                        const isExpanded = expandedGroups.has(group.id);
                        const GroupIcon = group.icon;
                        
                        return (
                            <div key={group.id} className="bg-card rounded-2xl border overflow-hidden">
                                <button
                                    onClick={() => toggleGroup(group.id)}
                                    className="w-full p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left"
                                >
                                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", group.color)}>
                                        <GroupIcon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1">
                                        <h2 className="font-semibold">{group.title}</h2>
                                        {group.description && (
                                            <p className="text-xs text-muted-foreground">{group.description}</p>
                                        )}
                                    </div>
                                    <span className="text-sm text-muted-foreground">
                                        {group.texts.length} Texte
                                    </span>
                                    <svg
                                        className={cn("h-5 w-5 text-muted-foreground transition-transform", isExpanded && "rotate-180")}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                
                                {isExpanded && (
                                    <div className="border-t p-4 space-y-4">
                                        {group.texts.map(text => (
                                            <div key={text.key} className="form-group">
                                                <Label className="form-label text-sm">{text.label}</Label>
                                                {text.multiline ? (
                                                    <Textarea
                                                        value={getTextValue(text.key)}
                                                        onChange={(e) => updateUiText(text.key, e.target.value)}
                                                        placeholder={text.placeholder}
                                                        className="form-input min-h-[80px]"
                                                        rows={3}
                                                    />
                                                ) : (
                                                    <Input
                                                        value={getTextValue(text.key)}
                                                        onChange={(e) => updateUiText(text.key, e.target.value)}
                                                        placeholder={text.placeholder}
                                                        className="form-input"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
